import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { useLayout } from 'context'
import { generateApplicationPacket, markVehiclesReadyForBuildABox } from 'controllers/ohioTitles/index'
import { OhioTitleApplicationReadyToPrintVehicleDto, OhioTitleDto } from 'dtos/ohioTitles'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, clientSidePagination, formatDateMoment } from 'utils'
import {
  ohioTitlesCompactButtonSx,
  ohioTitlesCompactCellSx,
  ohioTitlesCompactFormControlLabelSx,
  ohioTitlesDateCellSx,
  ohioTitlesTableContainerSx,
  ohioTitlesTableSx
} from './ohioTitlesTableStyles'

interface Props {
  vehicles: OhioTitleApplicationReadyToPrintVehicleDto[]
  setState: React.Dispatch<React.SetStateAction<OhioTitleDto | undefined>>
}

export default function ApplicationPacketReadyToPrintCard({ vehicles, setState }: Props) {
  const { setIsLayoutLoading } = useLayout()

  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof OhioTitleApplicationReadyToPrintVehicleDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [include, setInclude] = useState<boolean>(false)
  const [selected, setSelected] = useState<OhioTitleApplicationReadyToPrintVehicleDto[]>([])

  useEffect(() => {
    setPage(0)
  }, [pageSize, include])

  const onChangeSortByDirection = (propertyName: keyof OhioTitleApplicationReadyToPrintVehicleDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item sm={12} md={6}>
            <Typography fontWeight='bold' variant='h6'>
              Application Ready to Print
            </Typography>
          </Grid>

          <Grid item sm={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <FormControlLabel
              control={<Checkbox checked={include} onChange={() => setInclude(prev => !prev)} size='small' />}
              label='Include Application Packets Ready For Build a Box'
              sx={ohioTitlesCompactFormControlLabelSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TableContainer sx={ohioTitlesTableContainerSx}>
              <Table size='small' sx={ohioTitlesTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'></TableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='importerStockNumber'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Importer Stock #
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='exporterCompany'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Exporter
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='buyerCompany'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Buyer
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='papsNumber'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      PAPS #
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='signedBOLDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Signed BOL
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='vin'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      VIN
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='year'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Year
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='make'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Make
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='model'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Model
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='ohioTitleApplicationPacketReadyToPrintDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Application Packet Ready To Print
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleApplicationReadyToPrintVehicleDto>
                      keyName='ohioTitleApplicationPacketReadyForBuildABoxDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Application Packet Ready for Build a Box
                    </OrderableTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {vehicles
                    .filter(v => include || !v.ohioTitleApplicationPacketReadyForBuildABoxDateTime)
                    .sort(clientSideOrderBy<OhioTitleApplicationReadyToPrintVehicleDto>(orderBy, order))
                    .slice(...clientSidePagination(page, pageSize))
                    .map((vehicle, index) => (
                      <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            checked={selected.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                            onChange={() => {
                              if (selected.some(v => v.vehicleGuid === vehicle.vehicleGuid)) {
                                setSelected(selected.filter(v => v.vehicleGuid !== vehicle.vehicleGuid))
                              } else {
                                setSelected([...selected, vehicle])
                              }
                            }}
                            size='small'
                          />
                        </TableCell>

                        <TableCell sx={ohioTitlesCompactCellSx}>{vehicle.importerStockNumber ?? '-'}</TableCell>

                        <TableCell>{vehicle.exporterCompany}</TableCell>

                        <TableCell>{vehicle.buyerCompany ?? '-'}</TableCell>

                        <TableCell sx={ohioTitlesCompactCellSx}>{vehicle.papsNumber ?? '-'}</TableCell>

                        <TableCell sx={ohioTitlesDateCellSx}>{vehicle.signedBOLDateTime ? formatDateMoment(vehicle.signedBOLDateTime) : '-'}</TableCell>

                        <TableCell sx={ohioTitlesCompactCellSx}>{vehicle.vin}</TableCell>

                        <TableCell sx={ohioTitlesCompactCellSx}>{vehicle.year}</TableCell>

                        <TableCell>{vehicle.make}</TableCell>

                        <TableCell>{vehicle.model}</TableCell>

                        <TableCell sx={ohioTitlesDateCellSx}>
                          {vehicle.ohioTitleApplicationPacketReadyToPrintDateTime
                            ? formatDateMoment(vehicle.ohioTitleApplicationPacketReadyToPrintDateTime)
                            : '-'}
                        </TableCell>

                        <TableCell sx={ohioTitlesDateCellSx}>
                          {vehicle.ohioTitleApplicationPacketReadyForBuildABoxDateTime
                            ? formatDateMoment(vehicle.ohioTitleApplicationPacketReadyForBuildABoxDateTime)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={vehicles.filter(v => include || !v.ohioTitleApplicationPacketReadyForBuildABoxDateTime).length}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <Button
                color='primary'
                disabled={selected.length === 0}
                onClick={() => {
                  setIsLayoutLoading(true)
                  generateApplicationPacket(selected.map(v => v.vehicleGuid)).finally(() => setIsLayoutLoading(false))
                }}
                size='small'
                sx={ohioTitlesCompactButtonSx}
                variant='contained'
              >
                GENERATE APPLICATION PACKET FOR SELECTED VEHICLES
              </Button>
            </Grid>

            <Grid item>
              <Button
                color='primary'
                disabled={selected.length === 0}
                onClick={() => {
                  setIsLayoutLoading(true)
                  markVehiclesReadyForBuildABox(selected)
                    .then(res => {
                      enqueueSnackbar('Vehicle Application Packets are ready for Build A Box!', { variant: 'success' })
                      setState(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsLayoutLoading(false))
                }}
                size='small'
                sx={ohioTitlesCompactButtonSx}
                variant='contained'
              >
                MARK SELECTED VEHICLES APPLICATION PACKET READY FOR BUILD A BOX
              </Button>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Application Ready to Print Table:**

Displays all vehicles that are eligible to have their application marked as ready to print.

**Include Application Packets Ready For Build A Box Check Box:**

Displays the Application Packets Ready for Build A Box.

The checkbox is unchecked by default.

**Generate Application Packet For Selected Vehicles Button:**

This button is enabled after selecting at least one vehicle record.

Clicking this button will generate the Application Packet PDF for the selected vehicle(s).

The generated PDF will be downloaded directly to the user’s machine.

**Mark Selected Vehicles’ Application Packet Ready For Build A Box Button:**

This button is enabled after selecting at least one vehicle record.

Clicking this button will do the following:

   - Populates the “Application Packet Ready For Build A Box” column in the “Application Ready to Print” table with the current date.
   - Saves the current date and the user’s name in the “Application Packet Ready For Build A Box” field on the Ohio Title Application card under Vehicle Details – Titles (tab).
   - Generates individual PDFs for each selected vehicle, stored under Vehicle Details – Documents (tab) of the respective vehicle.

**Generated PDFs:**

The following documents are created for each selected vehicle:

   - BMV 3774: Application for Certificate of Title to a Motor Vehicle
   - BMV 4334: Out of State Vehicle Physical Inspection
   - BMV 3724: Odometer Disclosure Statement
   - Vehicle Purchase Agreement`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
